<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="handleNotificationClose"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-plus</v-icon>
        Invite Member
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- Member Type Selection -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Member Type</h3>
            </v-col>

            <v-col cols="12">
              <v-btn-toggle
                v-model="invite.memberType"
                mandatory
                class="mb-4"
                density="comfortable"
                color="#002060"
              >
                <v-btn value="Individual">Individual</v-btn>
                <v-btn value="Business">Business</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <!-- Individual Member Fields -->
          <v-row v-if="invite.memberType === 'Individual'">
            <v-col cols="12">
              <h3 class="section-title mb-4">Individual Member Details</h3>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="invite.firstName"
                label="Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'Name is required']"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="invite.surname"
                label="Surname"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-outline"
                :rules="[v => !!v || 'Surname is required']"
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="invite.membershipType"
                :items="membershipTypes"
                label="Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                :rules="[v => !!v || 'Membership type is required']"
              />
            </v-col>
          </v-row>

          <!-- Business Member Fields -->
          <v-row v-if="invite.memberType === 'Business'">
            <v-col cols="12">
              <h3 class="section-title mb-4">Business Member Details</h3>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="invite.businessName"
                label="Business Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-domain"
                :rules="[v => !!v || 'Business name is required']"
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="invite.businessMembershipType"
                :items="businessMembershipTypes"
                label="Select Business Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                :rules="[v => !!v || 'Business membership type is required']"
              />
            </v-col>
          </v-row>

          <!-- Contact Information -->
          <v-row>
            <v-col cols="12">
              <h3 class="section-title mb-4">Contact Information</h3>
              <p class="text-caption text-grey mb-2">Email is required to send invites. Phone number is optional.</p>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="invite.email"
                label="Email (Required)"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[v => !!v || 'Email is required']"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="invite.phone"
                label="Cellphone (Optional)"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                @input="formatPhoneInput"
                placeholder="074 551 1155"
              />
            </v-col>
          </v-row>

          <!-- Referral Information -->
          <v-row>
            <v-col cols="12">
              <h3 class="section-title mb-4">Referral Information (Optional)</h3>
            </v-col>

            <v-col cols="12" md="5">
              <v-text-field
                v-model="invite.referredByName"
                label="Referred by Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-multiple"
                placeholder="Enter referrer's name"
              />
            </v-col>

            <v-col cols="12" md="2" class="d-flex align-center justify-center">
              <span class="or-text">from</span>
            </v-col>

            <v-col cols="12" md="5">
              <v-text-field
                v-model="invite.companyName"
                label="Company Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-domain"
                placeholder="Enter company name"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                color="#002060"
                dark
                rounded
                size="large"
                :disabled="!valid || loading"
                :loading="loading"
                @click="sendInvite"
                class="mr-4"
              >
                <v-icon left>mdi-send</v-icon>
                {{ loading ? 'Sending...' : 'Send Invite' }}
              </v-btn>

              <v-btn
                color="grey"
                dark
                rounded
                size="large"
                @click="$router.back()"
              >
                <v-icon left>mdi-close</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';
import { md5 } from '../utils/md5';

export default {
  components: {
    NotificationDialog
  },
  data() {
    return {
      invite: {
        memberType: 'Individual',
        firstName: '',
        surname: '',
        businessName: '',
        membershipType: '',
        businessMembershipType: '',
        email: '',
        phone: '',
        notes: '',
        referredByName: '',
        companyName: ''
      },
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
              membershipTypes: ['Domestic', 'Individual', 'Individual & Domestic'],
      businessMembershipTypes: [
        { title: 'Blue Labour', value: 'Blue Labour' },
        { title: 'Silver Labour', value: 'Silver Labour' },
        { title: 'Gold Labour', value: 'Gold Labour' }
      ]
    };
  },
  computed: {
    ...mapGetters(['user']),
    phoneValidationRules() {
      return getPhoneValidationRules();
    }
  },
  created() {
    // Check if we're coming from a client details page
    if (this.$route.query.fromClient === 'true') {
      // Pre-fill the form with client data
      const query = this.$route.query;

      this.invite.memberType = query.memberType || 'Individual';
      this.invite.firstName = query.firstName || '';
      this.invite.surname = query.surname || '';
      this.invite.businessName = query.businessName || '';
      this.invite.email = query.email || '';
      this.invite.phone = query.phone || '';

      // Map membership type
      if (query.membershipType) {
        if (this.invite.memberType === 'Business') {
          // Map to business membership types
          const businessMapping = {
            'Blue Labour': 'Blue Labour',
            'Silver Labour': 'Silver Labour',
            'Gold Labour': 'Gold Labour'
          };
          this.invite.businessMembershipType = businessMapping[query.membershipType] || '';
        } else {
          // Map to individual membership types
          this.invite.membershipType = query.membershipType;
        }
      }
    }
  },
  methods: {
    formatPhoneInput() {
      this.invite.phone = formatPhoneInput(this.invite.phone);
    },

    onMemberTypeChange() {
      // Reset membership type when member type changes
      this.invite.membershipType = '';
    },
    async sendInvite() {
      console.log('sendInvite called');
      this.showNotification = false;
      this.loading = true;

      // Validate form
      if (!this.$refs.form.validate()) {
        this.loading = false;
        return;
      }

      // Check if at least one contact method is provided
      const hasEmail = this.invite.email && this.invite.email.trim();
      const hasPhone = this.invite.phone && this.invite.phone.trim();

      if (!hasEmail && !hasPhone) {
        this.loading = false;
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Please provide either an email or phone number.';
        this.showNotification = true;
        return;
      }

      // Validate required name fields based on member type
      if (this.invite.memberType === 'Individual') {
        if (!this.invite.firstName || !this.invite.surname) {
          this.loading = false;
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Please provide both name and surname for individual members.';
          this.showNotification = true;
          return;
        }
      } else if (this.invite.memberType === 'Business') {
        if (!this.invite.businessName) {
          this.loading = false;
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Please provide business name for business members.';
          this.showNotification = true;
          return;
        }
      }

      // If phone number is provided WITHOUT email, show error
      if (hasPhone && !hasEmail) {
        this.loading = false;
        this.notificationType = 'error';
        this.notificationTitle = 'Failed to Send!';
        this.notificationMessage = 'Phone number invites are not supported. Please use email.';
        this.showNotification = true;
        return;
      }

      // If no email is provided, show error
      if (!hasEmail) {
        this.loading = false;
        this.notificationType = 'error';
        this.notificationTitle = 'Email Required!';
        this.notificationMessage = 'Email is required to send an invite.';
        this.showNotification = true;
        return;
      }

      const contact = this.invite.email.trim();
      console.log('Contact to invite:', contact);
      try {
        // First save the invite to the database
        const inviteData = {
          memberType: this.invite.memberType,
          email: (this.invite.email || '').trim().toLowerCase(),
          phone: this.invite.phone || '',
          notes: this.invite.notes,
          status: 'Pending',
          dateAdded: new Date().toLocaleDateString('en-US'),
          createdAt: new Date().toISOString(),
          createdBy: this.user?.uid || 'unknown'
        };

        // Add member-specific data
        if (this.invite.memberType === 'Individual') {
          inviteData.firstName = this.invite.firstName;
          inviteData.surname = this.invite.surname;
          inviteData.membershipType = this.invite.membershipType;
        } else if (this.invite.memberType === 'Business') {
          inviteData.businessName = this.invite.businessName;
          inviteData.membershipType = this.invite.businessMembershipType;
        }

        // Add referral information
        inviteData.referredByName = this.invite.referredByName || '';
        inviteData.companyName = this.invite.companyName || '';

        // Compute and store stable sessionId (matches backend scheme)
        if (inviteData.email) {
          const emailLower = inviteData.email; // already lower-cased above
          const hash = md5(emailLower);
          inviteData.sessionId = `user_${hash.substring(0, 8)}`;
          inviteData.detailsUrl = `${window.location.origin}/details/${inviteData.sessionId}`;
        }

        // Save invite to DMM-LAW database
        console.log('Saving invite to DMM-LAW database...');
        console.log('Invite data to save:', inviteData);
        const docRef = await addDoc(collection(db, 'pendingInvites'), inviteData);
        const inviteId = docRef.id;
        console.log('✅ Successfully saved invite with ID:', inviteId);

        // Create the registration link with invite ID
        const registrationLink = `${window.location.origin}/details?invite=${inviteId}`;

        // Determine recipient name based on member type
        let recipientFullName = '';
        if (this.invite.memberType === 'Individual') {
          recipientFullName = `${this.invite.firstName || ''} ${this.invite.surname || ''}`.trim();
        } else if (this.invite.memberType === 'Business') {
          recipientFullName = this.invite.businessName || '';
        }

        // Debug logging
        console.log('Member type:', this.invite.memberType);
        console.log('First name:', this.invite.firstName);
        console.log('Surname:', this.invite.surname);
        console.log('Business name:', this.invite.businessName);
        console.log('Recipient full name:', recipientFullName);

        console.log('Sending fetch request to cloud function...');
        console.log('Invite ID to send:', inviteId);

        // Create registration URL with inviteId
        const registrationUrl = `https://online.dmmlaw.co.za/details?invite=${inviteId}`;
        console.log('Registration URL:', registrationUrl);

        const response = await fetch(
          'https://sendinviteemail-zjdy2tr2aa-uc.a.run.app',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: contact,
              subject: 'Welcome to the DMM Law Club - Your Invitation',
              recipientFullName: recipientFullName || 'Name Surname',
              referredByName: this.invite.referredByName || '',
              companyName: this.invite.companyName || '',
              detailsUrl: registrationUrl
            })
          }
        );
        console.log('Fetch response status:', response.status);
        const responseText = await response.text();
        console.log('Fetch response text:', responseText);
        if (response.ok) {
          // Log member invitation activity
          const invitedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.memberInvited(invitedBy, this.user?.uid, `Member - ${contact}`);

          // Show success notification
          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = 'Invite sent successfully!';
          this.showNotification = true;

          // Redirect to pending invites page immediately
          setTimeout(() => {
            this.$router.push('/members?type=pending-invite');
          }, 1500);
        } else {
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to send invite. Please try again.';
          this.showNotification = true;
        }
      } catch (e) {
        console.error('Error in sendInvite:', e);

        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to send invite. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
        console.log('sendInvite finished');
      }
    },

    handleNotificationClose() {
      this.showNotification = false;
      if (this.notificationType === 'success') {
        // Reset form after successful invite
        this.invite = {
          memberType: 'Individual',
          firstName: '',
          surname: '',
          businessName: '',
                  membershipType: '',
          businessMembershipType: '',
          email: '',
          phone: '',
          notes: ''
        };
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.text-grey {
  color: #666 !important;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style>
